import { useEffect, useEffectEvent } from 'react'

import type { Clinic, ClinicId } from '@snootsvet/shared'

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/frontend/components/ui/combobox'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useRegionClinics } from '@/frontend/features/region/use-region-clinics'
import { useUserSettings } from '@/frontend/hooks/use-user-settings'

interface ClinicSearchProps {
  id?: string
  /** The selected clinic ID (or null if none selected). */
  value: ClinicId | null
  /** Called with the clinic ID when a selection is made, or null when cleared. */
  onChange?: (clinicId: ClinicId | null) => void
  /** Called with the full clinic object when a selection is made, or null when cleared. */
  onClinicChange?: (clinic: Clinic | null) => void
  onBlur?: () => void
  disabled?: boolean
  placeholder?: string
  /** Whether the user can clear the selection. Defaults to `false`. */
  allowClear?: boolean
  includeArchived?: boolean
  'aria-invalid'?: boolean
  className?: string
}

/**
 * Generic clinic picker. Has no preferred-clinic default, but auto-selects the
 * sole option when the active region has exactly one clinic.
 */
function ClinicSearch({
  id,
  value,
  onChange,
  onClinicChange,
  onBlur,
  disabled,
  placeholder = 'Search clinics...',
  allowClear = false,
  includeArchived = false,
  className,
  ...props
}: ClinicSearchProps) {
  // Only offer clinics in the active region; an out-of-region selection resolves
  // to null here and simply shows as unselected.
  const clinics = useRegionClinics({ includeArchived })
  const selected = value ? clinics.find((c) => c.id === value) : null

  // Keep `value` in sync with what's currently selectable.
  useEffect(() => {
    // Clear a selection that isn't in the active region. (Callers holding a
    // persisted preference pass a value that's already region-scoped, so this is
    // a no-op for them.) On a region switch this nulls `value` in one commit; the
    // effect then re-runs on the next commit (`value` is a dep) to auto-select
    // below if the new region has a sole clinic.
    if (value && !clinics.some((c) => c.id === value)) {
      onChange?.(null)
      onClinicChange?.(null)
      return
    }
    // When the region has exactly one clinic there's no choice to make, so
    // auto-select it. The `value == null` guard prevents a re-select loop.
    // Skipped when `allowClear` is set, so we don't fight a user clearing it.
    if (!allowClear && clinics.length === 1 && value == null) {
      onChange?.(clinics[0].id)
      onClinicChange?.(clinics[0])
    }
  }, [value, clinics, allowClear, onChange, onClinicChange])

  return (
    <Combobox<Clinic>
      value={selected}
      onValueChange={(clinic) => {
        if (!clinic && !allowClear) return
        onChange?.(clinic ? clinic.id : null)
        onClinicChange?.(clinic ?? null)
      }}
      isItemEqualToValue={(a, b) => a.id === b.id}
      itemToStringLabel={(clinic) => clinic.name}
      items={clinics}
      disabled={disabled}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder}
        onBlur={onBlur}
        disabled={disabled}
        aria-invalid={props['aria-invalid']}
        showClear={allowClear}
        className={className}
      />
      <ComboboxContent className="min-w-80">
        <ComboboxList>
          <ComboboxEmpty>No clinics found.</ComboboxEmpty>
          <ComboboxCollection>
            {(clinic) => (
              <ComboboxItem key={clinic.id} value={clinic}>
                <div className="flex flex-col">
                  <span>{clinic.name}</span>
                  <span className="text-xs whitespace-nowrap text-muted-foreground">
                    {clinic.address}
                  </span>
                </div>
              </ComboboxItem>
            )}
          </ComboboxCollection>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

interface ClinicSearchWithDefaultProps extends ClinicSearchProps {}

/**
 * Clinic picker that auto-selects the user's preferred clinic (from settings)
 * on mount, falling back to the first clinic. Shows a skeleton while loading.
 */
function ClinicSearchWithDefault({
  value,
  onChange,
  onClinicChange,
  ...props
}: ClinicSearchWithDefaultProps) {
  const { settings } = useUserSettings()
  const { region } = useViewerRegion()
  // Only auto-apply the default clinic when it belongs to the active region, so
  // an out-of-region default isn't silently pre-selected on region-scoped screens.
  const preferred = settings?.defaultClinic
  const defaultClinic = preferred && preferred.region === region ? preferred : undefined

  const applyDefault = useEffectEvent((clinic: Clinic) => {
    if (value == null) {
      onChange?.(clinic.id)
      onClinicChange?.(clinic)
    }
  })

  useEffect(() => {
    if (!defaultClinic) return
    applyDefault(defaultClinic)
  }, [defaultClinic])

  return (
    <ClinicSearch value={value} onChange={onChange} onClinicChange={onClinicChange} {...props} />
  )
}

export { ClinicSearch, ClinicSearchWithDefault }
